"use client";

import { useEffect, useRef } from "react";
import { trackScrollDepth } from "@/app/lib/analytics";

const THRESHOLDS = [25, 50, 75, 100] as const;

/**
 * Tracks scroll depth at 25%, 50%, 75%, and 100% of the page.
 * Each threshold fires only once per page load.
 * Uses scroll position relative to total document height.
 *
 * Usage: call `useScrollDepth()` inside your page component. No JSX needed.
 */
export function useScrollDepth() {
  const firedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const fired = firedRef.current;

    function handleScroll() {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const percent = (scrollTop / docHeight) * 100;

      for (const threshold of THRESHOLDS) {
        if (percent >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          trackScrollDepth(threshold);
        }
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    // Check initial position (e.g. page reload mid-scroll)
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
}
